import { useEffect } from 'react';
import useOrderbookDisplayStore from './useOrderbookDisplayStore';
import useGetOraclePriceForMarket from 'src/hooks/useGetOraclePriceForMarket';
import { MarketId, l2ToDisplayBidsAndAsks } from '@drift/common';
import { ZERO } from '@drift-labs/sdk';
import { useTickSizeForMarket } from 'src/hooks/useGetTickSizeForMarket';
import { LiquidityType } from '@drift/common';
import useDevStore, { DevStore } from '../useDevStore';
import { useDynamicOrderbookDepth } from 'src/components/Orderbook/hooks/useDynamicOrderbookDepth';
import useDriftStore from '../DriftStore/useDriftStore';
import { getMarketPrecision } from 'src/utils/market';
import useCurrentUserBidsAndAsks from 'src/hooks/useCurrentUserBidsAndAsks';
import { useOrderbookDisplayPreference } from 'src/components/Orderbook/useOrderbookDisplayPreference';
import { ROW_HEIGHT } from 'src/components/Orderbook/OrderbookConstants';
import { MAX_ORDERBOOK_DEPTH } from 'src/components/Orderbook/OrderbookConstants';
import { useL2Orderbook } from 'src/hooks/query/orderbook/useL2Orderbook';

const getDisabledLiquidityTypes = (
	devDlobSettings: DevStore['dlobSettings']
): LiquidityType[] => {
	const disabledLiqTypes: LiquidityType[] = [];

	if (devDlobSettings.showDlob === false) {
		disabledLiqTypes.push('dlob');
	}
	if (devDlobSettings.showPhoenix === false) {
		disabledLiqTypes.push('phoenix');
	}
	if (devDlobSettings.showSerum === false) {
		disabledLiqTypes.push('serum');
	}
	if (devDlobSettings.showVamm === false) {
		disabledLiqTypes.push('vamm');
	}
	if (devDlobSettings.showIndicative === false) {
		disabledLiqTypes.push('indicative');
	}

	return disabledLiqTypes;
};

export const useSyncOrderbookDisplayState = () => {
	const marketId = useOrderbookDisplayStore((s) => s.marketId);
	const grouping = useOrderbookDisplayStore((s) => s.groupingSizeSelection);
	const devDlobSettings = useDevStore((s) => s.dlobSettings);
	const setOrderbookDisplayState = useOrderbookDisplayStore(
		(s) => s.setOrderbookDisplayState
	);

	const { data: l2Orderbook } = useL2Orderbook(marketId);

	const getOraclePriceForMarket = useGetOraclePriceForMarket();
	const tickSize = useTickSizeForMarket(marketId as MarketId);

	const { currentMarketBidsAndAsks } = useCurrentUserBidsAndAsks();

	const activeMobileView = useDriftStore((s) => s.activeMobileView);
	const { orderbookDisplayType } = useOrderbookDisplayPreference({
		isMini: activeMobileView === 'trade',
	});
	const { depth: dynamicDepth, containerCallbackRef } =
		useDynamicOrderbookDepth(orderbookDisplayType, ROW_HEIGHT);

	useEffect(() => {
		if (!marketId || !l2Orderbook) {
			return;
		}

		const oraclePrice = getOraclePriceForMarket(marketId)?.val ?? ZERO;
		const disabledLiquidityTypes = getDisabledLiquidityTypes(devDlobSettings);
		const basePrecisionToUse = getMarketPrecision(marketId);

		const displayBidsAndAsks = l2ToDisplayBidsAndAsks(
			l2Orderbook,
			oraclePrice,
			tickSize,
			grouping,
			currentMarketBidsAndAsks,
			basePrecisionToUse,
			disabledLiquidityTypes,
			MAX_ORDERBOOK_DEPTH,
			dynamicDepth
		);

		setOrderbookDisplayState({
			...displayBidsAndAsks,
			containerCallbackRef,
		});
	}, [
		marketId,
		devDlobSettings,
		l2Orderbook,
		tickSize,
		grouping,
		dynamicDepth,
		getOraclePriceForMarket,
		setOrderbookDisplayState,
		containerCallbackRef,
		currentMarketBidsAndAsks,
	]);
};
